"use client";

import { useState } from "react";
import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";
import { motion, AnimatePresence } from "motion/react";
import { Plus, Minus } from "lucide-react";

const faqKeys = ["faq.q1", "faq.q2", "faq.q4", "faq.q5", "faq.q6", "faq.q8"];

export function FAQ() {
  const { T } = useLang();
  const U = useUpper();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="sss" className="py-20 px-4 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-slate-900/50">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider">
            {U(T("faq.subtitle"))}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mt-3">
            {T("faq.title")}
          </h2>
        </motion.div>

        <div className="space-y-3">
          {faqKeys.map((qKey, i) => {
            const aKey = qKey.replace(".q", ".a");
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5, type: "spring", stiffness: 120 }}
                className="bg-white dark:bg-slate-900 rounded-xl border border-neutral-200 dark:border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-sm font-semibold text-neutral-900 dark:text-white pr-4">
                    {T(qKey)}
                  </span>
                  <div className="w-7 h-7 rounded-full bg-neutral-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                    {openIndex === i ? (
                      <Minus size={14} className="text-neutral-600 dark:text-slate-200" />
                    ) : (
                      <Plus size={14} className="text-neutral-600 dark:text-slate-200" />
                    )}
                  </div>
                </button>
                <AnimatePresence>
                  {openIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 text-sm text-neutral-600 dark:text-slate-200 leading-relaxed">
                        {T(aKey)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
